import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import { Dialog, DialogContent } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TableContainer from '@mui/material/TableContainer'
import Typography from '@mui/material/Typography'
import GroupsIcon from '@mui/icons-material/Groups'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'

import Pagination from '@/components/ui/pagination/Pagination'
import TextInput from '@/components/ui/inputs/TextInput'
import { PatientSourceMenu } from '@/components/ui/menu/PatientSourceMenu/PatientSourceMenu'

import { PatientSourceEnum } from '@/core/enums/patient-source.enum'

import { useIsSA } from '@/hooks/useIsSA'
import { useIsWebView, useIsMobile } from '@/hooks/useSizeBreakpoints'

import { InfoPatientPage } from '@/pages/patient'
import PatientsCardView from '@/pages/patients/ui/PatientsCardView'
import PatientsTable, { PatientData } from '@/pages/patients/ui/PatientsTable'

const rowsPerPageOptions = [5]

export type SelectPatientStepProps = {
  loading?: boolean
  multiSelect?: boolean
  onToggleGroupConsultation?: (isGroup: boolean) => void
  selectedPatientIds?: string[]
  /** Cached names for selected patients that may not be on the current page */
  selectedPatientNamesMap?: Record<string, string>
  /** SEHA (en-SA) only: creates and selects a placeholder emergency patient */
  onCreateEmergencyPatient?: () => void
  /**
   * SEHA (en-SA) only: whether the emergency patient created from this step is the
   * one currently selected. It is normally not on the visible page of the patient
   * table, so the button carries the selected state instead of a table row.
   */
  emergencyPatientSelected?: boolean
  onPageChange?: (newPage: number, rowsPerPage: number) => void
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  onSelectPatient?: (patientId: string) => void
  onSelectPatientSource?: (patientSource: PatientSourceEnum) => void
  patients: PatientData[]
  redirectToPatientProfile: (patientId: string) => void
  totalPatients: number
}

export function SelectPatientStep(props: SelectPatientStepProps) {
  const {
    loading = false,
    multiSelect = false,
    onToggleGroupConsultation,
    selectedPatientIds = [],
    selectedPatientNamesMap = {},
    onCreateEmergencyPatient,
    emergencyPatientSelected = false,
    onPageChange,
    onSearch,
    onSelectPatient,
    onSelectPatientSource,
    patients,
    redirectToPatientProfile,
    totalPatients,
  } = props
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const isSA = useIsSA()
  const isWebView = useIsWebView()

  const [page, setPage] = useState<number>(1)
  const [patientId, setPatientId] = useState('')
  const [patientProfileDialog, setPPDialog] = useState(false)
  const [patientSource, setPatientSource] = useState(PatientSourceEnum.MedconnectPatient)

  const onOpen = (patient: PatientData) => {
    if (isWebView) {
      setPPDialog(true)
      setPatientId(patient._id)
    } else redirectToPatientProfile(patient._id)
  }

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage, rowsPerPageOptions[0])
  }

  const handleSearchPatient = useDebouncedCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(1)
    onSearch(event)
  }, 300)

  const handleSelectPatient = (patientData: PatientData) => {
    onSelectPatient?.(patientData._id)
  }

  // Get patient names for selected chips in group mode
  const selectedPatientNames = multiSelect
    ? selectedPatientIds.map((id) => {
        const patient = patients.find((p) => p._id === id)
        return patient ? `${patient.name} ${patient.surname}` : (selectedPatientNamesMap[id] || id)
      })
    : []

  return (
    <>
      <Container maxWidth="md">
        <Stack spacing={2}>
          <Stack direction="row" spacing={3} alignItems="center">
            <Box flex={1}>
              <TextInput
                id="search-patient"
                fullWidth
                onChange={handleSearchPatient}
                placeholder={t('page.createConsultation.step.patientSelection.searchInput.placeholder')}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={multiSelect}
                  onChange={(e) => onToggleGroupConsultation?.(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <GroupsIcon fontSize="small" />
                  <Typography variant="body2">{t('page.createConsultation.step.patientSelection.groupConsultation')}</Typography>
                </Stack>
              }
            />
            <PatientSourceMenu
              onChange={(patientSource) => {
                setPage(1)
                setPatientSource(patientSource)
                onSelectPatientSource?.(patientSource)
              }}
              value={patientSource}
            />
            {isSA && onCreateEmergencyPatient && (
              <Button
                id="create-emergency-patient"
                aria-pressed={emergencyPatientSelected}
                color={emergencyPatientSelected ? 'success' : 'primary'}
                startIcon={emergencyPatientSelected ? <CheckCircleIcon /> : undefined}
                variant={emergencyPatientSelected ? 'contained' : 'outlined'}
                onClick={onCreateEmergencyPatient}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                {emergencyPatientSelected
                  ? t('page.createConsultation.step.patientSelection.emergencyPatientButton.selectedLabel')
                  : t('page.createConsultation.step.patientSelection.emergencyPatientButton.label')}
              </Button>
            )}
          </Stack>
          {multiSelect && selectedPatientIds.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {selectedPatientNames.map((name, index) => (
                <Chip
                  key={selectedPatientIds[index]}
                  label={name}
                  color="primary"
                  onDelete={() => onSelectPatient?.(selectedPatientIds[index])}
                />
              ))}
              <Chip
                label={`${selectedPatientIds.length} ${t('page.createConsultation.step.patientSelection.patientsSelected')}`}
                variant="outlined"
                color="info"
              />
            </Stack>
          )}
          <>
            {isMobile ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                  <Pagination
                    color="primary"
                    itemsPerPageOptions={rowsPerPageOptions}
                    shape="rounded"
                    page={page}
                    onPageChange={handleChangePage}
                    totalItems={totalPatients}
                  />
                </Box>
                <PatientsCardView data={patients} loading={loading} onSelect={handleSelectPatient} />
              </Box>
            ) : (
              <TableContainer component={Paper} id="patient-table">
                <PatientsTable
                  autoHightlight={!multiSelect}
                  loading={loading}
                  data={patients}
                  onSelect={handleSelectPatient}
                  onOpen={onOpen}
                  selectedIds={multiSelect ? selectedPatientIds : undefined}
                />
                <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                  <Pagination
                    color="primary"
                    disableChangeRowsPerPage
                    itemsPerPageOptions={rowsPerPageOptions}
                    shape="rounded"
                    page={page}
                    onPageChange={handleChangePage}
                    totalItems={totalPatients}
                  />
                </Box>
              </TableContainer>
            )}
          </>
        </Stack>
      </Container>
      <Dialog
        onClose={() => setPPDialog(false)}
        open={patientProfileDialog}
        PaperProps={{
          sx: {
            minWidth: '800px',
          },
        }}
      >
        <DialogContent>
          <InfoPatientPage patientId={patientId} />
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SelectPatientStep
